/**
 * Documentation Panel for Home Layout Manager
 *
 * Comprehensive guide covering:
 * - Home screen layout structure
 * - Horizontal scrollers and positioning
 * - Category carousel configuration
 * - Banner management
 * - Draft/publish workflow
 *
 * @module HomeLayoutManager/components/DocumentationPanel
 */
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  ViewCarousel as ViewCarouselIcon,
  Storefront as StorefrontIcon,
  Home as HomeIcon,
} from '@mui/icons-material';

interface DocumentationPanelProps {
  onClose: () => void;
}

export default function DocumentationPanel({ onClose }: DocumentationPanelProps): React.ReactElement {
  return (
    <Paper sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Content Control Guide</Typography>
        <Button size="small" onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        This tool manages the home screen layout using a draft/publish workflow. Changes are saved
        to a draft and only go live when you click "Publish Changes".
      </Alert>

      {/* Home Screen Layout Structure */}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Home Screen Layout Structure</Typography>
      <Typography variant="body2" paragraph>
        The mobile app home screen displays content in this order:
      </Typography>
      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2, fontFamily: 'monospace', fontSize: 12 }}>
        <pre style={{ margin: 0 }}>
{`[Address Bar]
[Email Verification Banner - if needed]
[Search Bar]
[Top Banner]
[Slider Banners - auto-rotating]

========= PRODUCTS FIRST BATCH =========
[New & Popular] (horizontal scroller)
[Category 1] (horizontal scroller)
[Category 2] (horizontal scroller)

========= CATEGORIES CAROUSEL =========
[Category Icon Carousel]
"View All Categories" button

========= PRODUCTS SECOND BATCH =========
[Category 3] (horizontal scroller)
[Category 4] (horizontal scroller)
...remaining categories...

========= BRANDS SECTION =========
[Brands Carousel]
"View All Brands" button

[Bottom Banner]`}
        </pre>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Horizontal Scrollers */}
      <Typography variant="h6" sx={{ mb: 1 }}>Horizontal Scrollers (Product Sections)</Typography>
      <Typography variant="body2" paragraph>
        Horizontal scrollers show products on the home screen. Each active category with
        <strong> "Show on Home Page"</strong> enabled becomes a horizontal scroller.
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>Positioning:</strong> Categories are split around the icon carousel:
      </Typography>
      <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
        <li><strong>First batch:</strong> First 2-3 categories appear ABOVE the icon carousel</li>
        <li><strong>Second batch:</strong> Remaining categories appear BELOW the icon carousel</li>
        <li><strong>New & Popular:</strong> Always appears first (locked at position 1). Click to configure which products appear — by tag, category, brand, or the default auto filter.</li>
        <li><strong>Brands:</strong> Always appears last</li>
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        The order of categories determines the order of horizontal scrollers. Drag to reorder!
      </Alert>

      <Divider sx={{ my: 2 }} />

      {/* Filter Types */}
      <Typography variant="h6" sx={{ mb: 1 }}>Filter Types for Home Page Scrollers</Typography>
      <Typography variant="body2" paragraph>
        When enabling "Show on Home Page", choose how products are filtered:
      </Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableBody>
          <TableRow>
            <TableCell><strong>By Category</strong></TableCell>
            <TableCell>Shows products from this specific category (default)</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>By Tag</strong></TableCell>
            <TableCell>Shows products with a specific tag (e.g., "special", "organic")</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>By Brand</strong></TableCell>
            <TableCell>Shows products from a specific brand</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>New & Popular (auto)</strong></TableCell>
            <TableCell>Shows products marked as popular in Vend (default for the locked first block)</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>Combined</strong></TableCell>
            <TableCell>Mix of categories AND tags together (advanced)</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>Icon Only (None)</strong></TableCell>
            <TableCell>Just shows the icon in carousel, no product scroller</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      {/* Badge Reference */}
      <Typography variant="h6" sx={{ mb: 1 }}>Badge Reference</Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableBody>
          <TableRow>
            <TableCell><Chip icon={<ViewCarouselIcon />} label="Carousel" size="small" color="error" /></TableCell>
            <TableCell>Shows in mobile home page icon carousel (aim for 4-8 icons)</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Chip icon={<ViewCarouselIcon />} label="Web" size="small" color="info" /></TableCell>
            <TableCell>Shows in web home page icon carousel</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Chip icon={<HomeIcon />} label="Home" size="small" color="warning" /></TableCell>
            <TableCell>Product scroller on home page (horizontal scroller)</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Chip icon={<StorefrontIcon />} label="Shop" size="small" color="primary" /></TableCell>
            <TableCell>Shows in shop page category grid</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Chip label="Early Cutoff" size="small" color="warning" variant="outlined" /></TableCell>
            <TableCell>Respects 6pm cutoff (liquor, age-restricted items)</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      {/* Category Carousel */}
      <Typography variant="h6" sx={{ mb: 1 }}>Category Icon Carousel</Typography>
      <Typography variant="body2" paragraph>
        The icon carousel shows category icons that users can tap to navigate. Configuration:
      </Typography>
      <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
        <li><strong>Mobile:</strong> Enable "Show in Carousel" for mobile app</li>
        <li><strong>Web:</strong> Enable "Show in Carousel (Web)" for web app</li>
        <li><strong>Optimal count:</strong> 4-8 icons for mobile (avoids sparse look)</li>
        <li><strong>Order:</strong> Icons appear in the same order as the category list</li>
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Categories only appear in the carousel at stores where they have products in stock.
        Use the preview to check each store's carousel.
      </Alert>

      <Divider sx={{ my: 2 }} />

      {/* Banners */}
      <Typography variant="h6" sx={{ mb: 1 }}>Banner Management</Typography>
      <Typography variant="body2" paragraph>
        Three banner positions are available:
      </Typography>
      <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
        <li><strong>Top Banner:</strong> Static banner above the slider (1 item max)</li>
        <li><strong>Slider Banner:</strong> Auto-rotating carousel (up to 7 items)</li>
        <li><strong>Bottom Banner:</strong> Static banner at the bottom (1 item max)</li>
      </Typography>
      <Typography variant="body2" paragraph>
        Banner items can link to: categories, products, external URLs, or deep links.
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Draft/Publish Workflow */}
      <Typography variant="h6" sx={{ mb: 1 }}>Draft/Publish Workflow</Typography>
      <Typography variant="body2" paragraph>
        All changes are saved to a draft first. This prevents accidental changes to the live app.
      </Typography>
      <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
        <li><strong>Edit:</strong> Make changes freely - they're saved to the draft</li>
        <li><strong>Preview:</strong> See how changes will look on different devices/stores</li>
        <li><strong>Compare:</strong> Toggle "Compare Mode" to see draft vs live side-by-side</li>
        <li><strong>Publish:</strong> Click "Publish Changes" to push draft to live</li>
        <li><strong>Reset:</strong> Click "Reset" to discard draft and reload from live</li>
        <li><strong>Rollback:</strong> Use history to revert to a previous published state</li>
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Collaboration */}
      <Typography variant="h6" sx={{ mb: 1 }}>Collaboration</Typography>
      <Typography variant="body2" paragraph>
        Multiple admins can view this page simultaneously. You'll see presence indicators
        showing who else is viewing or editing. Coordinate changes to avoid conflicts.
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Early Cutoff */}
      <Typography variant="h6" sx={{ mb: 1 }}>Early Cutoff</Typography>
      <Typography variant="body2" paragraph>
        Categories with <strong>"Respects Early Cutoff"</strong> enabled will not be available for
        ordering after 6pm (liquor license requirement). Enable this for:
      </Typography>
      <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
        <li>Liquor / Wine / Beer / Spirits</li>
        <li>Any age-restricted products</li>
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>Public Holiday Mode:</strong> When a store enables "Public Holiday Mode" in store settings,
        the early cutoff time applies to <strong>ALL categories</strong> for that day.
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Icon Requirements */}
      <Typography variant="h6" sx={{ mb: 1 }}>Icon Requirements</Typography>
      <Typography variant="body2" paragraph>
        <strong>Dimensions:</strong> 78x78 pixels (displayed at this exact size)<br />
        <strong>Format:</strong> PNG (preferred for transparency) or JPG<br />
        <strong>Background:</strong> Transparent or solid red (#FF002C)<br />
        <strong>Max size:</strong> 500KB
      </Typography>
    </Paper>
  );
}
